/**
 * AlertRow — one compact alert ping (extracted from ActionCenter in the TASK-575 Two-Box
 * redesign so the NOTICE column / ChangeRadar's Alerts tab can reuse it). Behavior unchanged
 * (severity chip · client link → canvas · quick-actions · snooze · dismiss) plus a make-task
 * "→ Task" button — the one signal→action bridge.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellOff, Clock, ListPlus, X } from 'lucide-react';
import AlertBadge from './AlertBadge';
import { useCanvasActions } from '../../../stores/canvasStore';
import { formatDeadline } from '../../../utils/formatDeadline';
import type { DashboardAlert } from '../../../types/dashboard';
import { namesAClient } from '../../../utils/clientDisplayName';
import { policyPermits } from '../../../utils/signalPolicy';
import { useViewerRole } from '../../../contexts/AccountContext';

// TASK-923: exported so the GROUP snooze offers the same durations as the row. Two lists
// would be two answers to "how long is a deferral".
// ponytail: exporting non-components here trips react-refresh/only-export-components; a
// `snoozeDurations.ts` is the tidy home. One warning, zero errors — move it when something
// else needs these.
export const SNOOZE_OPTIONS = [
  { label: '1 day', days: 1 },
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
];

export const addDays = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
};

function alertAge(iso: string): string {
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
  if (h < 1) return '<1h';
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

interface AlertRowProps {
  alert: DashboardAlert;
  onDismiss: (id: string) => void;
  onSnooze: (id: string, until: string) => void;
  onMakeTask?: (alert: DashboardAlert) => void;
  /**
   * TASK-819: "open this alert" — mark it read, and for a transaction-monitoring alert open
   * its review modal. The Alerts page used to hang this off the per-alert card it routed
   * one-member groups to; that route is gone, and without this prop the transaction modal
   * would have become unreachable as a side effect of a layout change.
   */
  onSelect?: (alert: DashboardAlert) => void;
}

export const AlertRow: React.FC<AlertRowProps> = ({ alert, onDismiss, onSnooze, onMakeTask, onSelect }) => {
  const navigate = useNavigate();
  const { openClient } = useCanvasActions();
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  // TASK-732: actions render FROM the server policy — no local source-event rule table.
  // A payload without a policy (older backend) keeps every control, as before.
  //
  // TASK-923 review: this row was the SIXTH policy surface and the one left ungated, which
  // made it the worst of the six — `AlertGroupCard`'s fallback toast tells the advisor to
  // "expand the group and open a signal", and expanding renders exactly these rows. An
  // advisor followed that instruction to a live Snooze button that 403s.
  const role = useViewerRole();
  const allows = (action: string) => policyPermits(alert.actionPolicy, action, role);
  // Deep review #5: this row never read `alert.deadline`, so a TASK-672 meeting reminder — whose
  // entire point is WHEN the meeting is — showed no time at all on the Today dashboard. The only
  // number on the row was `alertAge(createdAt)`, which is the age of the ALERT, not the meeting.
  const deadlineInfo = formatDeadline(alert.deadline);
  // TASK-784, ported from `AlertCard` (see below). Shown only when the policy actually allows
  // the disposition it recommends.
  const recommended = alert.actionPolicy?.recommended_disposition ?? null;
  const showRecommendation = !!recommended && allows(recommended);
  const recommendationLabel = recommended
    ? recommended.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
    : '';

  return (
    <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
      <div className="flex items-start gap-2">
        <AlertBadge level={alert.level} size="sm" showLabel={false} />
        {/* TASK-841: a UUID-shaped name is treated as NO name, so this falls to the title
            branch below — the same thing the row already does for an alert that never
            carried one. `groupAlerts` applies the identical rule for the grouped view; this
            is the ungrouped one, and it renders straight from the column. */}
        {namesAClient(alert.relatedClientName, alert.relatedClientId) ? (
          <button
            type="button"
            onClick={() => alert.relatedClientId && openClient(alert.relatedClientId)}
            className="flex-1 truncate pt-0.5 text-left text-xs font-medium leading-tight text-gray-800 transition-colors hover:text-kielis-blue"
          >
            {alert.relatedClientName}
          </button>
        ) : (
          <span className="flex-1 truncate pt-0.5 text-xs font-medium text-gray-800">
            {alert.title}
          </span>
        )}
        <span className="shrink-0 pt-0.5 text-[10px] text-gray-400">{alertAge(alert.createdAt)}</span>
        {allows('dismiss') && (
          <button
            type="button"
            onClick={() => onDismiss(alert.id)}
            className="shrink-0 text-gray-400 transition-colors hover:text-gray-700"
            aria-label="Dismiss alert"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {alert.relatedClientName &&
        (onSelect ? (
          // TASK-819 (review finding 1): the FIRST cut hung `onSelect` off the header title,
          // which only renders when the alert has NO client name — i.e. almost never. Every
          // client-scoped alert took the `openClient` branch, so the transaction-review modal
          // this prop exists to keep reachable was still dead, and `markAsRead` never fired
          // for a named-client alert. The client button keeps opening the client; the TITLE
          // is what opens the alert.
          <button
            type="button"
            data-testid="alert-row-select"
            onClick={() => onSelect(alert)}
            className="block w-full text-left text-[12px] font-medium leading-tight text-gray-900 transition-colors hover:text-kielis-blue"
          >
            {alert.title}
          </button>
        ) : (
          <p className="text-[12px] font-medium leading-tight text-gray-900">{alert.title}</p>
        ))}
      {alert.description && (
        <p className="line-clamp-2 text-[11.5px] leading-relaxed text-gray-600">{alert.description}</p>
      )}
      {deadlineInfo && (
        <p
          data-testid="alert-deadline"
          className={`flex items-center gap-1 text-[11.5px] ${
            deadlineInfo.isOverdue ? 'font-semibold text-red-600' : 'text-gray-600'
          }`}
        >
          <Clock className="h-3 w-3 shrink-0" aria-hidden />
          <span>
            {deadlineInfo.absolute} · {deadlineInfo.relative}
          </span>
        </p>
      )}

      {/*
        TASK-819 (review finding 4): `actionRequired`, `aiInsight` and the TASK-784
        recommendation were rendered ONLY by `AlertCard`. Routing every alert through the
        group card left `AlertCard` with no live importer, so those three disappeared from the
        running app — `recommended_disposition` in particular is set on every portfolio policy
        precisely so a surface could propose an action. Ported here rather than left to rot.
      */}
      {alert.actionRequired && (
        <p data-testid="alert-row-action-required" className="text-[11.5px] font-medium text-gray-800">
          {alert.actionRequired}
        </p>
      )}
      {alert.aiInsight && (
        <p data-testid="alert-row-insight" className="text-[11.5px] italic leading-relaxed text-gray-500">
          {alert.aiInsight}
        </p>
      )}
      {showRecommendation && (
        // A LABEL, not a button: this row has no disposition handler, and a button that does
        // nothing is worse than a label because it implies the RM acted (TASK-784 / DEC-3).
        <p data-testid="alert-recommendation" className="flex items-center gap-1.5 text-[11px]">
          <span className="uppercase tracking-wide text-gray-500">Recommended</span>
          <span
            data-testid="alert-recommendation-label"
            className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-700"
          >
            {recommendationLabel}
          </span>
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {onMakeTask && allows('assign') && (
          <button
            type="button"
            onClick={() => onMakeTask(alert)}
            className="flex items-center gap-1 rounded-lg bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white transition-colors hover:bg-indigo-700"
            title="Create a task from this alert"
          >
            <ListPlus className="h-3 w-3" />
            Task
          </button>
        )}
        {alert.quickActions?.map((qa) => (
          <button
            key={qa.id}
            type="button"
            onClick={() => navigate(qa.action)}
            className={`rounded-lg px-2.5 py-1 text-[11px] font-medium transition-colors ${
              qa.type === 'primary'
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : qa.type === 'danger'
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {qa.label}
          </button>
        ))}
        {allows('snooze') && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setSnoozeOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={snoozeOpen}
            className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-[11px] text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800"
          >
            <BellOff className="h-3 w-3" />
            Snooze
          </button>
          {snoozeOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setSnoozeOpen(false)} aria-hidden />
              <div
                role="menu"
                className="absolute left-0 top-full z-20 mt-1 min-w-[104px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
              >
                {SNOOZE_OPTIONS.map((o) => (
                  <button
                    key={o.days}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSnoozeOpen(false);
                      onSnooze(alert.id, addDays(o.days));
                    }}
                    className="block w-full rounded px-2 py-1 text-left text-[11px] text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default AlertRow;
